import type { SubmitEvent } from 'react'
import type { FavoriteViewSearch } from '@/contexts/favorites/favoriteView'
import { ShellBffHttpError } from '@offer/bff-client-core'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DEFAULT_FOLDER_EMOJI_UNIFIED } from '@/components/FavoriteViews/FavoriteViewEmoji'
import { ALL_SAVED_VIEWS_ID } from '@/components/FavoriteViews/FavoriteViewFolderCard'
import { useToasterContext } from '@/contexts/toaster/useToasterContext'
import { useCreateFavoriteView, useCreateFavoriteViewFolder, useFavoriteViewFolders, useFavoriteViews } from '@/hooks/favorites/useFavoriteViews'

interface UseFavoriteViewFormOptions {
  search: FavoriteViewSearch | null
  onClose: () => void
}

export function useFavoriteViewForm({ search, onClose }: UseFavoriteViewFormOptions) {
  const { t } = useTranslation()
  const { showToast } = useToasterContext()
  const foldersQuery = useFavoriteViewFolders()
  const favoriteViewsQuery = useFavoriteViews()
  const createFolder = useCreateFavoriteViewFolder()
  const createView = useCreateFavoriteView()
  const [title, setTitle] = useState('')
  const [isCreatingFolder, setIsCreatingFolder] = useState(false)
  const [selectedFolderId, setSelectedFolderId] = useState(ALL_SAVED_VIEWS_ID)
  const [newFolderName, setNewFolderName] = useState('')
  const [selectedEmoji, setSelectedEmoji] = useState(DEFAULT_FOLDER_EMOJI_UNIFIED)

  const activeFolderId = isCreatingFolder ? '' : selectedFolderId
  const isFolderSelectionValid = isCreatingFolder
    ? Boolean(newFolderName.trim() && selectedEmoji)
    : Boolean(activeFolderId)
  const isSelectedPhysicalFolder = activeFolderId !== ALL_SAVED_VIEWS_ID
  const isSaving = createFolder.isPending || createView.isPending
  const isSaveDisabled = !search
    || !title.trim()
    || !isFolderSelectionValid
    || (!isCreatingFolder && isSelectedPhysicalFolder && (foldersQuery.isLoading || foldersQuery.isError))
    || isSaving

  const resetFolderDraft = () => {
    setIsCreatingFolder(false)
    setSelectedFolderId(ALL_SAVED_VIEWS_ID)
    setNewFolderName('')
    setSelectedEmoji(DEFAULT_FOLDER_EMOJI_UNIFIED)
  }

  const closePanel = () => {
    setTitle('')
    resetFolderDraft()
    onClose()
  }

  const cancelNewFolder = () => {
    resetFolderDraft()
  }

  const addNewFolder = () => {
    setIsCreatingFolder(true)
    setSelectedFolderId('')
    setNewFolderName('')
    setSelectedEmoji(DEFAULT_FOLDER_EMOJI_UNIFIED)
  }

  const selectFolder = (folderId: string) => {
    setSelectedFolderId(folderId)
    setIsCreatingFolder(false)
  }

  const handleSubmit = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isSaveDisabled || !search)
      return

    let folderId: string | undefined = activeFolderId === ALL_SAVED_VIEWS_ID ? undefined : activeFolderId
    const creatingFolder = isCreatingFolder

    try {
      if (creatingFolder) {
        const folder = await createFolder.mutateAsync({ name: newFolderName.trim(), emoji: selectedEmoji })
        folderId = folder.id
        setIsCreatingFolder(false)
        setSelectedFolderId(folder.id)
        setNewFolderName('')
      }

      await createView.mutateAsync({
        title: title.trim(),
        ...(folderId ? { folderId } : {}),
        search,
      })
      showToast({ variant: 'success', message: t('favorite-views.save-success') })
      closePanel()
    }
    catch (error) {
      if (creatingFolder && folderId) {
        setIsCreatingFolder(false)
        setSelectedFolderId(folderId)
      }

      const messageKey = creatingFolder && !folderId && error instanceof ShellBffHttpError && error.status === 409
        ? 'favorite-views.errors.folder-conflict'
        : 'favorite-views.errors.save'
      showToast({ variant: 'error', message: t(messageKey) })
    }
  }

  return {
    activeFolderId,
    addNewFolder,
    cancelNewFolder,
    closePanel,
    draft: {
      isCreating: isCreatingFolder,
      name: newFolderName,
      selectedEmoji,
    },
    folders: foldersQuery.data?.items ?? [],
    handleSubmit,
    hasFolderLoadError: foldersQuery.isError,
    isSaving,
    isSaveDisabled,
    onDraftNameChange: setNewFolderName,
    onEmojiSelect: setSelectedEmoji,
    onFolderSelect: selectFolder,
    setTitle,
    title,
    totalViews: favoriteViewsQuery.data?.total ?? 0,
  }
}
